import React from "react";
import { Button } from "@/components/ui/button";
import { Crown, Sparkles, ArrowUpRight, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { FeatureSlug } from "@/lib/feature-gates";
import { featureSlugToName } from "@/lib/feature-gates";

interface FeatureSectionPaywallProps {
  featureSlug: FeatureSlug;
  description?: string;
  children: React.ReactNode;
  planName?: string | null;
  isLocked?: boolean;
  isLoading?: boolean;
  onUpgrade?: () => void;
  upgradePath?: string;
}

const FeatureSectionPaywall: React.FC<FeatureSectionPaywallProps> = ({
  featureSlug,
  description,
  children,
  planName,
  isLocked = true,
  isLoading = false,
  onUpgrade,
  upgradePath,
}) => {
  const featureName = featureSlugToName[featureSlug];
  const navigate = useNavigate();

  const showPaywall = !isLoading && isLocked;

  const handleUpgrade =
    onUpgrade ?? (() => navigate(upgradePath ?? "/dashboard/my-account"));

  if (!showPaywall) {
    return <>{children}</>;
  }

  return (
    <div className="relative overflow-hidden rounded-xl">
      <div className="pointer-events-none select-none" aria-hidden="true">
        {children}
      </div>

      <div className="absolute inset-0 bg-gradient-to-b from-background/5 via-background/60 to-background" />

      <div className="absolute inset-0 flex items-start justify-center pt-12 p-4">
        <div className="max-w-xs w-full animate-fade-in-up">
          <div className="rounded-xl border border-border/50 bg-card/95 backdrop-blur-xl p-5 shadow-lg text-center space-y-3">
            <div className="mx-auto w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 border border-amber-300/30 flex items-center justify-center shadow-md shadow-amber-500/15">
              <Crown className="h-5 w-5 text-white" />
            </div>

            <div className="space-y-1">
              <h4 className="text-sm font-bold text-foreground">
                {featureName}
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {description ||
                  `Este recurso não está disponível no seu plano atual.`}
              </p>
              {planName && (
                <div className="flex items-center justify-center gap-1">
                  <Lock className="h-2.5 w-2.5 text-muted-foreground/60" />
                  <span className="text-[10px] text-muted-foreground/60">
                    Disponível em planos superiores ao {planName}
                  </span>
                </div>
              )}
            </div>

            <Button
              size="sm"
              className="w-full gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md shadow-amber-500/20 text-xs h-8"
              onClick={handleUpgrade}
            >
              <Sparkles className="h-3 w-3" />
              Ver Planos
              <ArrowUpRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureSectionPaywall;
